import { useCallback, useRef } from 'react'
import { onScroll, copyTo, recordAfterWrite } from './syncedScrollX'

// FAR-83 #13: keeps the Capacity and SKU Plan scroll boxes at the same
// sideways position. Returns two stable ref callbacks; each card puts its
// one on its scroll box. The rules are in syncedScrollX.js.
export function useSyncedScrollX() {
  const nodes = useRef({ capacity: null, grid: null })
  // What we last wrote to each box, until its scroll event (the echo) arrives.
  const written = useRef({ capacity: null, grid: null })

  const attach = useCallback((key, otherKey, node) => {
    if (!node) return undefined
    const write = (k, value) => {
      const n = nodes.current[k]
      const before = n.scrollLeft
      n.scrollLeft = value
      written.current[k] = recordAfterWrite(before, n.scrollLeft)
    }

    nodes.current[key] = node
    written.current[key] = null
    // Mounting (e.g. Capacity shown again): start where the other box is.
    const start = copyTo(nodes.current[otherKey]?.scrollLeft ?? null, node.scrollLeft)
    if (start != null) write(key, start)

    const handle = () => {
      const other = nodes.current[otherKey]
      const d = onScroll(node.scrollLeft, written.current[key], other ? other.scrollLeft : null)
      written.current[key] = null
      if (d.write != null) write(otherKey, d.write)
    }
    node.addEventListener('scroll', handle, { passive: true })
    return () => {
      node.removeEventListener('scroll', handle)
      if (nodes.current[key] === node) {
        nodes.current[key] = null
        written.current[key] = null
      }
    }
  }, [])

  const capacityRef = useCallback((node) => attach('capacity', 'grid', node), [attach])
  const gridRef = useCallback((node) => attach('grid', 'capacity', node), [attach])
  return { capacityRef, gridRef }
}
